import { useSettingsStore } from '@starsoft/common/hooks/useSettingsStore';
import { CommonSettings } from '@starsoft/common/interfaces';
import { Trans as ITrans } from '@starsoft/common/interfaces/trans';
import { ComponentType } from 'react';

/**
 * TransWithDefault Component
 * This component is responsible for rendering a translation component with default fallback.
 * It utilizes the settings store to determine the appropriate translation component to use.
 *
 * @param {ITrans} props - The properties for the component.
 * @param {object} props.components - Custom components to be used within the translation text.
 * @param {string} props.i18nKey - The internationalization key for the translation.
 * @param {object} props.values - The values to be interpolated in the translation string.
 *
 * @returns {JSX.Element} The rendered translation component with provided components and props.
 *
 * @example
 * // Usage example:
 * <TransWithDefault
 *   components={{ strong: <strong />, br: <br /> }}
 *   i18nKey="example_key"
 *   values={{ name: 'John' }}
 * />
 */
export function TransWithDefault({ components, ...props }: ITrans) {
  const { state } = useSettingsStore();
  const settings: CommonSettings = state.settings;
  const Trans: ComponentType<ITrans> = settings.translationComponents.Trans;

  // Render the Trans component with provided components and props
  return (
    <Trans
      components={{
        strong: <strong />,
        br: <br />,
        p: <p />,
        ul: <ul />,
        li: <li />,
        ...components,
      }}
      {...props}
    />
  );
}
